// CC: extracted from integrations.component.ts -> Exchange popup section
import _ from 'lodash';
import {Component, EventEmitter, NgZone, OnInit, Output} from "@angular/core";
import {GeneralService} from '../../../../../services/generalService';
import {AuthService} from '../../../../../services/authService';
import {DICTIONARY} from '../../../../../dictionary';
import {RouteService} from '../../../../../services/routeService';
import {NotificationService} from '../../../../../services/notificationService';

@Component({
	selector: 'exchange-integration-component',
	templateUrl: './exchange-integration.component.html',
})
export class ExchangeIntegrationComponent implements OnInit {
	@Output() connectionChanged = new EventEmitter<void>();
	dic = DICTIONARY;
	planInfo: any;
	userInfo: any;
	selectedServerEndpoint: { name: string; AzureAD: string; MSGraphAPI: string; };
	integrationActionInProcess = false;
	integImportGroupsSummary = false;
	showGroups = false;
	showAliases = false;
	changeEndPointPopup: any;
	updateCloudAPIEndpointInProcess = false;
	graphChecks: any[];
	graphCheckInProcess = false;
	customCredentialsEndpoints = [DICTIONARY.CONSTANTS.exchangeServerEndpoints.USGovernmentGCCHigh.name, DICTIONARY.CONSTANTS.exchangeServerEndpoints.USGovernmentDoD.name, DICTIONARY.CONSTANTS.exchangeServerEndpoints.China.name, DICTIONARY.CONSTANTS.exchangeServerEndpoints.SingleTenantApp.name];

	constructor(public gs: GeneralService, private authService: AuthService,
				private rs: RouteService, private ns: NotificationService,
				private ngZone: NgZone) {}

	ngOnInit() {
		this.userInfo = this.gs.userInfo;
		this.getIntegrations();
	}

	// CC: taken from integrations.component.ts -> getIntegrations (Exchange-specific parts)
	getIntegrations = () => {
		return this.rs.getIntegrations().then((planInfo) => {
			this.planInfo = planInfo;
			this.selectedServerEndpoint = _.clone(this.dic.CONSTANTS.exchangeServerEndpoints.Global);
			if (this.planInfo.exchange_server?.endpoint_name) {
				this.selectedServerEndpoint.name = this.planInfo.exchange_server.endpoint_name;
				this.selectedServerEndpoint.AzureAD = this.planInfo.exchange_server.azuread_endpoint;
				this.selectedServerEndpoint.MSGraphAPI = this.planInfo.exchange_server.msgraph_endpoint;
			}
			if (!this.selectedServerEndpoint.AzureAD) {
				this.selectedServerEndpoint.AzureAD = DICTIONARY.CONSTANTS.exchangeServerEndpoints.Global.AzureAD;
			}
		});
	}

	isConnected = () => {
		return !!(this.planInfo?.exchange_server?.tid && this.planInfo?.exchange_server?.enabled);
	}

	graphCheckCount = (status) => {
		return this.graphChecks?.filter(check => check.status === status).length || 0;
	}

	troubleshootGraphAccess = () => {
		this.graphChecks = null;
		this.graphCheckInProcess = true;
		this.rs.troubleshootGraphAccess().then((checks) => {
			this.graphChecks = checks;
			this.graphCheckInProcess = false;
		}, (err) => {
			console.error(err);
			this.graphCheckInProcess = false;
		});
	}

	// CC: taken from integrations.component.ts -> connectToExchange
	connectToExchange = () => {
		if (this.isConnected()) {
			this.gs.showPopup({
				title: 'Disconnect from mail server',
				subTitle: `Please note - you are about to cancel the connection to your mail server`,
				body: ["Groups will not be automatically updated", "Inbound Shield will be disabled for all mailboxes"],
				type: this.dic.CONSTANTS.popupWarning,
				doneBtnText: 'Disconnect',
				doneCb: () => {
					this.integrationActionInProcess = true;
					let notIdx = this.ns.showInfoMessage('Disconnecting from Exchange server...', {timeout: 0});
					this.rs.disconnectExchange().then(() => {
						this.ns.overwriteMessage(notIdx, 'Successfully disconnected from Exchange server');
						this.integrationActionInProcess = false;
						this.getIntegrations();
						this.connectionChanged.emit();
					}, () => {
						this.integrationActionInProcess = false;
					});
				}
			});
		}
		else {
			if (this.planInfo.gsuite_server?.customer_id && this.planInfo.gsuite_server?.enabled) {
				this.ns.showErrorMessage(DICTIONARY.ERRORS.authDisconnectIntegration.replace('%s', 'Google Workspace'));
				return;
			}
			// CC: single-tenant cloud credentials are wiped on disconnect - ask for them again before consent
			if (this.customCredentialsEndpoints.includes(this.selectedServerEndpoint.name) && !this.planInfo.exchange_server?.client_id) {
				this.changeEndpoint(this.selectedServerEndpoint);
				return;
			}

			this.integrationActionInProcess = true;
			this.authService.authMicrosoftExchangeAdmin(this.userInfo.email, this.selectedServerEndpoint.AzureAD, this.planInfo.exchangeAdminAppId, 'admin', this.planInfo.exchange_server?.tid, (err, onPremises) => {
				if (err) {
					if (err !== true) {
						this.ns.showWarnMessage(err);
					}
				}
				else {
					if (onPremises) {
						this.ns.showWarnMessage(this.dic.MESSAGES.exchangeServerOnPremises);
					}
					this.ns.showInfoMessage('Successfully connected to Microsoft Exchange');
				}

				this.ngZone.run(() => {
					this.integrationActionInProcess = false;
					this.getIntegrations();
					this.connectionChanged.emit();
				});
			});
		}
	}

	// CC: taken from integrations.component.ts -> connectToExchangeAdvanced
	connectToExchangeAdvanced = () => {
		this.gs.showPopup({
			title: 'Grant Advanced Permissions',
			subTitle: `You are about to grant Trustifi advanced permissions to your Exchange Office365 tenant. Trustifi will be able to:`,
			type: this.dic.CONSTANTS.popupWarning,
			body: ['Reset user mailbox passwords', 'Disable user mailbox accounts', 'Monitor user mailbox rules', 'View user mailbox sign-ins'],
			doneBtnText: 'Grant',
			doneCb: () => {
				this.integrationActionInProcess = true;
				this.authService.authMicrosoftExchangeAdmin(this.userInfo.email, this.selectedServerEndpoint.AzureAD, this.planInfo.exchangeAdminAdvancedAppId, 'advanced', this.planInfo.exchange_server?.tid, (err, onPremises) => {
					if (err) {
						if (err !== true) {
							this.ns.showWarnMessage(err);
						}
					}
					else {
						if (onPremises) {
							this.ns.showWarnMessage(this.dic.MESSAGES.exchangeServerOnPremises);
						}
						this.ns.showInfoMessage('Successfully connected to Microsoft Exchange');
					}

					this.ngZone.run(() => {
						this.integrationActionInProcess = false;
						this.getIntegrations();
					});
				});
			}
		});
	}

	// CC: taken from integrations.component.ts -> changeEndpoint
	changeEndpoint = (endpoint) => {
		const exchangeServer = this.planInfo.exchange_server || {};
		const usesCustomCredentials = this.customCredentialsEndpoints.includes(endpoint.name);
		if (this.selectedServerEndpoint.name === endpoint.name && !usesCustomCredentials) {
			return;
		}
		if (usesCustomCredentials) {
			this.changeEndPointPopup = {
				show: true,
				applyInProcess: false,
				endpoint,
				subTitle: `Configure credentials for ${endpoint.name}`,
				clientIdInput: exchangeServer.client_id || '',
				clientSecretInput: '',
				clientSecretSet: !!exchangeServer.client_secret_set
			};
			return;
		}

		let azureADInput: any;
		let microsoftGraphAPIInput: any;
		if (endpoint.name === this.dic.CONSTANTS.exchangeServerEndpoints.Hybrid.name) {
			azureADInput = '';
			microsoftGraphAPIInput = '';
		}

		this.changeEndPointPopup = {
			show: true,
			applyInProcess: false,
			subTitle: `Please note - you are about to change the cloud API endpoint of your mail server to ${endpoint.name}`,
			azureADInput: azureADInput,
			microsoftGraphAPIInput: microsoftGraphAPIInput,
			doneCb: () => {
				this.changeEndPointPopup.applyInProcess = true;
				const nextEndpoint = _.clone(endpoint);
				if (this.changeEndPointPopup.azureADInput !== undefined && this.changeEndPointPopup.microsoftGraphAPIInput !== undefined) {
					if (this.changeEndPointPopup.azureADInput === "" || this.changeEndPointPopup.microsoftGraphAPIInput === "") {
						this.changeEndPointPopup.applyInProcess = false;
						return this.ns.showErrorMessage("Please enter a valid endpoint");
					}

					if (this.changeEndPointPopup.azureADInput.indexOf(' ') > -1 || this.changeEndPointPopup.microsoftGraphAPIInput.indexOf(' ') > -1) {
						this.changeEndPointPopup.applyInProcess = false;
						return this.ns.showErrorMessage(this.dic.ERRORS.invalidServerEndpoint);
					}
					nextEndpoint.AzureAD = this.changeEndPointPopup.azureADInput;
					nextEndpoint.MSGraphAPI = this.changeEndPointPopup.microsoftGraphAPIInput;
				}
				this.updateCloudAPIEndpoint(nextEndpoint);
			}
		};
	}

	saveSovereignCloud = () => {
		const popup = this.changeEndPointPopup;
		const exchangeServer = this.planInfo.exchange_server || {};
		const sameCloud = exchangeServer.endpoint_name === popup.endpoint.name;
		const sameMainCredentials = sameCloud && exchangeServer.client_id?.toLowerCase() === popup.clientIdInput?.toLowerCase();
		if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(popup.clientIdInput || '')) {
			return this.ns.showErrorMessage('The client ID must be a valid GUID.');
		}
		if (!popup.clientSecretInput && (!popup.clientSecretSet || !sameMainCredentials)) {
			return this.ns.showErrorMessage('Enter the client secret for this cloud and app registration.');
		}

		popup.applyInProcess = true;
		this.updateCloudAPIEndpoint({
			name: popup.endpoint.name,
			client_id: popup.clientIdInput,
			client_secret: popup.clientSecretInput
		});
	}

	// CC: taken from integrations.component.ts -> updateCloudAPIEndpoint
	updateCloudAPIEndpoint = (endpoint: any = this.selectedServerEndpoint) => {
		if (!endpoint?.name) {
			return;
		}

		this.integrationActionInProcess = true;
		this.updateCloudAPIEndpointInProcess = true;
		this.rs.activateInboundEmailRelayAndGenKey({
			action: this.dic.CONSTANTS.inboundRelayActions.cloudAPIEndpoint,
			endpoint
		}).then(() => this.getIntegrations()).then(() => {
			this.ns.showInfoMessage(this.dic.MESSAGES.changedSuccessfully);
			this.updateCloudAPIEndpointInProcess = false;
			this.integrationActionInProcess = false;
			this.changeEndPointPopup.show = false;
			this.connectionChanged.emit();
		}, () => {
			this.updateCloudAPIEndpointInProcess = false;
			this.integrationActionInProcess = false;
			this.changeEndPointPopup.applyInProcess = false;
		});
	}

	// CC: taken from integrations.component.ts -> integImportGroups (Exchange case)
	integImportGroups = () => {
		if (!this.isConnected()) {
			return;
		}

		this.integrationActionInProcess = true;
		this.rs.createGroupSubscription().then((response: any) => {
			this.integrationActionInProcess = false;
			this.integImportGroupsSummary = true;
			if (response?.summary) {
				this.ns.showInfoMessage(response.summary);
			}
		}, () => {
			this.integrationActionInProcess = false;
		});
	}

	getGroups = (show) => {
		this.showGroups = show;
	}

	getMailboxesAliases = (show) => {
		this.showAliases = show;
	}
}
